/**
 * Central data-modell + aggregator för Regelbanken.
 * Varje kategori har sin egen fil med regler; index samlar och exponerar
 * lookup-funktioner för routes.
 */
import { pblRules } from "./pbl";
import { bbrRules } from "./bbr";
import { attefallRules } from "./attefall";
import { strandskyddRules } from "./strandskydd";
import { bygglovRules } from "./bygglov";
import { detaljplanRules } from "./detaljplan";
import { vaRules } from "./va";

export const CATEGORIES = {
  pbl: {
    slug: "pbl",
    label: "Plan- och bygglagen",
    short: "PBL",
    h1: "Plan- och bygglagen (PBL) – alla regler för bygglov",
    metaDescription:
      "Komplett guide till PBL kapitel för kapitel. Bygglov, marklov, rivningslov, tillsyn och kontrollplan enligt Plan- och bygglagen 2010:900.",
    intro:
      "Plan- och bygglagen (PBL) är den centrala lagen som styr allt byggande i Sverige. Här hittar du kapitelvisa förklaringar med exempel, vanliga fallgropar och kommunala avvikelser.",
    icon: "ScrollText",
    rules: pblRules,
  },
  bbr: {
    slug: "bbr",
    label: "Boverkets byggregler",
    short: "BBR",
    h1: "Boverkets byggregler (BBR) – krav, mått och undantag",
    metaDescription:
      "BBR förklarad enkelt: brand, tillgänglighet, energi, buller och hygien. Krav på villa, attefallshus, garage och tillbyggnad.",
    intro:
      "Boverkets byggregler (BBR) preciserar de tekniska egenskapskraven i PBL. Vi går kapitel för kapitel med praktiska exempel för villaägare och entreprenörer.",
    icon: "BookOpen",
    rules: bbrRules,
  },
  attefall: {
    slug: "attefall",
    label: "Attefall",
    short: "Attefall",
    h1: "Attefall – alla regler, mått och undantag",
    metaDescription:
      "Attefallshus, attefallstillbyggnad och attefallstakkupa. Mått, höjder, avstånd till tomtgräns, strandskydd och skillnad bygglov vs anmälan.",
    intro:
      "Attefallsreglerna gör att du kan bygga max 30 m² komplementbyggnad eller 15 m² tillbyggnad utan bygglov – men bara om alla villkor är uppfyllda. Här är hela regelverket förklarat.",
    icon: "Home",
    rules: attefallRules,
  },
  strandskydd: {
    slug: "strandskydd",
    label: "Strandskydd",
    short: "Strandskydd",
    h1: "Strandskydd – regler, undantag och dispenser",
    metaDescription:
      "100-metersregeln, särskilda skäl, dispensprocess och bygglov nära vatten. Så söker du strandskyddsdispens 2026.",
    intro:
      "Strandskyddet gäller 100 m från strandlinjen (utökat till 300 m på vissa platser). Innan du bygger eller ändrar tomten behöver du ofta dispens – här förklaras hur.",
    icon: "Waves",
    rules: strandskyddRules,
  },
  bygglov: {
    slug: "bygglov",
    label: "Bygglovstyper",
    short: "Bygglov",
    h1: "Bygglov – alla bygglovstyper och regler",
    metaDescription:
      "Bygglov för altan, pool, garage, carport, plank, solceller, tillbyggnad, fasadändring m.m. Mått, kostnader, handläggningstid och vanliga fel.",
    intro:
      "Här listar vi alla vanliga bygglovstyper – från altan och pool till solceller och rivningslov. Varje sida ger dig mått, ritningskrav, kostnad och kommunala skillnader.",
    icon: "Building2",
    rules: bygglovRules,
  },
  detaljplan: {
    slug: "detaljplan",
    label: "Detaljplaner",
    short: "Detaljplan",
    h1: "Detaljplan – regler, tolkning och undantag",
    metaDescription:
      "Så läser du en detaljplan: prickmark, byggnadsarea, höjdbegränsningar och användningsbestämmelser. Guide 2026.",
    intro:
      "Detaljplanen bestämmer vad och hur mycket du får bygga på din tomt. Vi går igenom vanliga beteckningar och hur du tolkar planbestämmelserna korrekt.",
    icon: "Map",
    rules: detaljplanRules,
  },
  va: {
    slug: "va",
    label: "VA & Avlopp",
    short: "VA",
    h1: "VA & Avlopp – enskilt avlopp, dricksvatten och tillstånd",
    metaDescription:
      "Enskilt avlopp, dricksvattenbrunn, sluten tank och markarbete för VA. Regler, kostnader och tillstånd 2026.",
    intro:
      "På landsbygden och i skärgården saknas ofta kommunalt VA. Här förklarar vi allt om enskilt avlopp, dricksvattenbrunnar och tillstånd som krävs innan du gräver eller borrar.",
    icon: "Droplets",
    rules: vaRules,
  },
};

export const CATEGORY_ORDER = ["bygglov", "attefall", "strandskydd", "va", "pbl", "bbr", "detaljplan"];

/** Slår upp regel via kategori + slug. Returnerar null om inte finns. */
export function findRule(categorySlug, ruleSlug) {
  const cat = CATEGORIES[categorySlug];
  if (!cat) return null;
  const rule = cat.rules.find((r) => r.slug === ruleSlug);
  if (!rule) return null;
  return { rule, category: cat };
}

/** Alla regler platta för sitemap-generering. */
export function allRuleUrls() {
  const urls = [];
  for (const slug of CATEGORY_ORDER) {
    const cat = CATEGORIES[slug];
    urls.push(`/${cat.slug}`);
    for (const r of cat.rules) {
      urls.push(`/${cat.slug}/${r.slug}`);
    }
  }
  return urls;
}

/** Hittar relaterade regler baserat på related_slugs (formatet "category/slug"). */
export function resolveRelated(relatedSlugs = []) {
  const out = [];
  for (const path of relatedSlugs) {
    const [cat, slug] = path.split("/");
    const found = findRule(cat, slug);
    if (found) {
      out.push({
        path: `/${cat}/${slug}`,
        title: found.rule.h1 || found.rule.title,
        category: found.category.label,
      });
    }
  }
  return out;
}
